import React from "react";
import { Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import "./userRoleAdmin.css";
import { DashboardUserManagementSection } from "./users/DashboardUserManagementSection.tsx";
import { AdministrationSectionHeader } from "../../pages/administration/AdministrationSectionHeader.tsx";
import { canReadModule, isSystemAdmin } from "../../utils/appRole.js";
import { useModuleCrudPermissions } from "../../hooks/useModuleCrudPermissions.js";

/**
 * Full users + roles CRUD UI backed by `/api/v1/admin/users` and `/api/v1/admin/roles` (see `store/localUserRoleStore.tsx`).
 */
export function UserRoleAdminPanel() {
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const { read: canReadUsers } = useModuleCrudPermissions("users");
  const showRolesTab = useMemo(() => {
    if (!accountAccess) return true;
    return isSystemAdmin(accountAccess) || canReadModule(accountAccess, "roles");
  }, [accountAccess]);
  const showUsersTab = useMemo(() => {
    if (!accountAccess) return true;
    return isSystemAdmin(accountAccess) || canReadUsers;
  }, [accountAccess, canReadUsers]);

  const [tab, setTab] = useState<"users" | "roles">("users");

  useEffect(() => {
    if (!showRolesTab && tab === "roles") setTab("users");
    if (!showUsersTab && tab === "users" && showRolesTab) setTab("roles");
  }, [showRolesTab, showUsersTab, tab]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minHeight: 0,
        flex: 1,
        width: "100%",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            minHeight: 480,
            height: "auto",
            maxHeight: "none",
            flex: 1,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AdministrationSectionHeader
            title="User Management"
            infoTooltip="Create and manage users, assign business roles, and configure per-camera permissions. Data is loaded from the admin API."
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: { xs: 1, sm: 2 }, pb: 1, fontFamily: "Roboto, sans-serif" }}
          >
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: { xs: 1, sm: 2 },
              borderBottom: 1,
              borderColor: "divider",
              minHeight: 42,
              "& .MuiTab-root": { textTransform: "none" },
            }}
          >
            {showUsersTab ? <Tab label="Users" value="users" /> : null}
            {showRolesTab ? <Tab label="Roles & permissions" value="roles" /> : null}
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: { xs: 1, sm: 2 } }}>
            <DashboardUserManagementSection activeTab={tab} />
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
}
